import React, { useMemo } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import {
	faAngleDown,
	faInfoCircle as faInfoCircleLight,
	faQuestionCircle,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faAngleUp, faClock, faEye, faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { Avatar, Tooltip } from "~/ui";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { BillingContext } from "../account/Billing/BillingContext";
import { HighlightSection } from "./HighlightsPanel";
import { useIsPublic, useWatchStatsFeatureFlagOn, useWatchStatsShowPercentageFeatureFlagOn } from "./utils";

import type {
	StatsPanel_note$data as Note,
	StatsPanel_note$key as NoteKey,
} from "~/__generated__/StatsPanel_note.graphql";
import type { StatsPanel_viewer$key as ViewerKey } from "~/__generated__/StatsPanel_viewer.graphql";
import type { StatsPanelRecapQuery, StatsPanelRecapQuery$data } from "~/__generated__/StatsPanelRecapQuery.graphql";
import type { TranscriptModalContentStats$data as TranscriptModalContentStats } from "~/__generated__/TranscriptModalContentStats.graphql";

import styles from "./css/StatsPanel.module.css";

const MAX_USERS_SHOWN = 5;

function dateStringPlusNSeconds(dateString: string, seconds: number): Date {
	const start = new Date(dateString);
	start.setTime(start.getTime() + seconds * 1000);
	return start;
}

export interface StatsPanelRef {
	handleStatsUpdated: () => void;
}

export const StatsPanel = ({
	meetingRecording,
	note: noteKey,
	viewer: viewerKey,
}: {
	meetingRecording: TranscriptModalContentStats;
	note: NoteKey | null;
	viewer: ViewerKey;
}) => {
	const isPublic = useIsPublic();
	const note = useFragment<NoteKey>(
		graphql`
			fragment StatsPanel_note on Note {
				eventStart
				eventEnd
				account {
					allowRecapEngagementStats
				}
			}
		`,
		noteKey,
	);

	const viewer = useFragment<ViewerKey>(
		graphql`
			fragment StatsPanel_viewer on Viewer {
				user {
					isGuest
				}
			}
		`,
		isPublic ? null : viewerKey,
	);

	const { analyticsFingerprint } = React.useContext(BillingContext);
	React.useEffect(
		() => dispatchAnalyticsTrack("[Fellow Copilot] Meeting stats viewed", { fingerprint: analyticsFingerprint }),
		[analyticsFingerprint],
	);

	const isWatchStatsEnabled = useWatchStatsFeatureFlagOn();
	const allowRecapEngagementStats = note?.account?.allowRecapEngagementStats && !viewer?.user?.isGuest && !isPublic;

	return (
		<article className={styles.statsPanel}>
			<div className={styles.statsContainer}>
				<ActualTimes note={note} meetingRecording={meetingRecording} />
				{isWatchStatsEnabled && allowRecapEngagementStats && (
					<React.Suspense fallback={<LoadingSpinner size="xs" inline dimmer={false} delay={150} />}>
						<RecapEngagementSection meetingRecordingOrClipId={meetingRecording.id} />
					</React.Suspense>
				)}
				<ParticipationSection participation={meetingRecording.meetingStats?.userStats} />
				<AttendanceSection
					attendance={meetingRecording.meetingStats?.userStats}
					recordingStart={meetingRecording.meetingCallStart}
				/>
			</div>
			<div className={styles.disclaimer}>
				<FontAwesomeIcon icon={faInfoCircle} />
				Some people talk more. Others share a mic in hybrid meetings. This data is best used for understanding your
				whole organization&apos;s use of the Note Taker, not evaluating an individual&apos;s performance.
			</div>
		</article>
	);
};

const ActualTimes = ({
	note,
	meetingRecording,
}: {
	note: Note | null;
	meetingRecording: TranscriptModalContentStats;
}) => {
	const { eventStart, eventEnd } = note ?? {
		eventStart: meetingRecording.meetingCallStart,
		eventEnd: meetingRecording.meetingCallEnd,
	};
	const { meetingCallStart: recordingStart } = meetingRecording;
	const { actualStartTime: participationStartSeconds, actualEndTime: participationEndSeconds } =
		meetingRecording.meetingStats ?? {};

	if (participationStartSeconds === undefined || participationEndSeconds === undefined || !eventEnd || !eventStart) {
		return <></>;
	}

	const actualStartTime = dateStringPlusNSeconds(recordingStart, participationStartSeconds);
	const actualStartTimeStr = actualStartTime.toLocaleTimeString("en-CA", { timeStyle: "short" });
	const actualStartDiffMins = Math.floor((actualStartTime.getTime() - new Date(eventStart).getTime()) / 60000);

	const actualEndTime = dateStringPlusNSeconds(recordingStart, participationEndSeconds);
	const actualEndTimeStr = actualEndTime.toLocaleTimeString("en-CA", { timeStyle: "short" });
	const actualEndDiffMins = Math.floor((new Date(eventEnd).getTime() - actualEndTime.getTime()) / 60000);

	const meetingDurationSeconds = (new Date(eventEnd).getTime() - new Date(eventStart).getTime()) / 1000;
	const actualStartPercentage =
		actualStartDiffMins <= 0 ? 0 : ((actualStartDiffMins * 60) / meetingDurationSeconds) * 100;
	const actualEndPercentage =
		actualEndDiffMins <= 0
			? 0
			: actualEndDiffMins * 60 >= meetingDurationSeconds
				? 100
				: ((actualEndDiffMins * 60) / meetingDurationSeconds) * 100;
	const actualDurationPercentage = 100 - actualEndPercentage - actualStartPercentage;

	return (
		<div className={styles.actualTimes}>
			<div className={styles.actualTimesMeta}>
				<h4>
					Start time{" "}
					<Tooltip
						className={styles.infoTooltip}
						content="The moment that the Note Taker detected discussion starting."
					>
						<FontAwesomeIcon icon={faInfoCircleLight} />
					</Tooltip>
				</h4>
				<h4>
					<Tooltip
						className={styles.infoTooltip}
						content="The moment when the last speaker in the call finished their sentence."
					>
						<FontAwesomeIcon icon={faInfoCircleLight} />
					</Tooltip>{" "}
					End time
				</h4>
			</div>
			<div className={styles.scheduledMeetingBar}>
				{/* if the meeting starts after it's scheduled to end, don't show the bar */}
				{actualStartPercentage <= 100 && (
					<div
						className={styles.actualTimesBar}
						style={{
							left: `${actualStartPercentage}%`,
							width: actualDurationPercentage <= 0 ? "1px" : `${actualDurationPercentage}%`,
						}}
					/>
				)}
			</div>
			<div className={styles.actualTimesMeta}>
				<p>
					Started{" "}
					{actualStartDiffMins == 0
						? "on time"
						: `${Math.abs(actualStartDiffMins)} min ${actualStartDiffMins > 0 ? "late" : "early"}`}
				</p>
				<p>
					Ended{" "}
					{actualEndDiffMins == 0
						? "on time"
						: `${Math.abs(actualEndDiffMins)} min ${actualEndDiffMins > 0 ? "early" : "late"}`}
				</p>
			</div>
			<div className={styles.actualTimesMeta}>
				<p className={styles.actualTime}>{actualStartTimeStr}</p>
				<p className={styles.actualTime}>{actualEndTimeStr}</p>
			</div>
		</div>
	);
};

const IconContainer = ({
	icon,
	containerClassName,
	iconClassName,
}: {
	icon: IconDefinition;
	containerClassName?: string;
	iconClassName?: string;
}) => {
	return (
		<div className={cx(styles.iconContainer, containerClassName)}>
			<FontAwesomeIcon icon={icon} className={cx(styles.iconContainerIcon, iconClassName)} />
		</div>
	);
};

const StatisticsCard = ({
	title,
	icon,
	children: value,
	iconContainerClassName,
	iconClassName,
}: {
	title: string;
	icon: IconDefinition;
	children: React.ReactNode;
	iconContainerClassName?: string;
	iconClassName?: string;
}) => {
	return (
		<div className={styles.statisticsCard}>
			<div className={styles.statisticsCardContent}>
				<p className={styles.statisticsCardTitle}>{title}</p>
				<p className={styles.statisticsCardValue}>{value}</p>
			</div>
			<IconContainer icon={icon} containerClassName={iconContainerClassName} iconClassName={iconClassName} />
		</div>
	);
};

const formatTimeSaved = (minutes: number): string => {
	let minutesFloored = Math.floor(minutes);

	if (minutesFloored < 60) {
		return `${minutesFloored} minute${minutesFloored !== 1 ? "s" : ""}`;
	} else {
		const hours = Math.floor(minutesFloored / 60);
		return `${hours} hour${hours !== 1 ? "s" : ""}`;
	}
};

const TIME_SAVED_TOOLTIP_CONTENT =
	"This is the time saved by users who viewed the recap instead of attending the live meeting.";

const RecapEngagementStatsCards = ({
	totalViewCount,
	minutesSaved,
	hasLegacyViews,
}: {
	totalViewCount: number | undefined;
	minutesSaved: number | undefined;
	hasLegacyViews: boolean | undefined;
}) => {
	// Hide cards if any of the values are 0 or undefined
	if (totalViewCount === undefined || totalViewCount == 0) {
		return null;
	}

	if (minutesSaved === undefined || minutesSaved == 0) {
		return null;
	}

	return (
		<div>
			<div className={styles.statisticsCards}>
				{!hasLegacyViews && (
					<StatisticsCard
						title="Total time saved"
						icon={faClock}
						iconContainerClassName={styles.timeSavedIconContainer}
						iconClassName={styles.timeSavedIcon}
					>
						{formatTimeSaved(minutesSaved)}
					</StatisticsCard>
				)}

				<StatisticsCard
					title="Total views"
					icon={faEye}
					iconContainerClassName={styles.viewsIconContainer}
					iconClassName={styles.viewsIcon}
				>
					{totalViewCount} view{totalViewCount === 1 ? "" : "s"}
				</StatisticsCard>
			</div>
			{!!minutesSaved && !hasLegacyViews && (
				<div className={styles.howDoWeCalculate}>
					How do we calculate this?
					<Tooltip content={TIME_SAVED_TOOLTIP_CONTENT}>
						<FontAwesomeIcon icon={faQuestionCircle} />
					</Tooltip>
				</div>
			)}
		</div>
	);
};
type UserStatsType = NonNullable<TranscriptModalContentStats["meetingStats"]>["userStats"] | undefined;

type RecapEngagementViewersType = NonNullable<
	Extract<
		NonNullable<StatsPanelRecapQuery$data["node"]>,
		{ __typename?: "MeetingRecording" | "MeetingRecordingClip" }
	>["recapEngagementStats"]
>["viewers"];

export const RecapEngagementSection = ({ meetingRecordingOrClipId }: { meetingRecordingOrClipId: string }) => {
	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => setIsCollapsed(!isCollapsed), [isCollapsed]);
	const sectionRef = React.useRef<HTMLElement>(null);

	const query = graphql`
		query StatsPanelRecapQuery($id: ID!) {
			node(id: $id) {
				... on MeetingRecordingInterface {
					__typename
					... on MeetingRecording {
						recapEngagementStats {
							viewers {
								user {
									email
									fullName
									image
								}
								displayName
								viewCount
								watchPercentage
							}
							totalViewCount
							minutesSaved
							hasLegacyViews
						}
					}

					... on MeetingRecordingClip {
						recapEngagementStats {
							viewers {
								user {
									email
									fullName
									image
								}
								displayName
								viewCount
								watchPercentage
							}
							totalViewCount
							minutesSaved
							hasLegacyViews
						}
					}
				}
			}
		}
	`;

	const clipOrMeetingRecording = useLazyLoadQuery<StatsPanelRecapQuery>(query, { id: meetingRecordingOrClipId }).node;
	const isClip = clipOrMeetingRecording?.__typename === "MeetingRecordingClip";

	// We need to make sure that the type is not %other because it will not have recapEngagementStats
	const isValidGraphQLType = clipOrMeetingRecording?.__typename === "MeetingRecording" || isClip;

	const viewers: RecapEngagementViewersType = useMemo(() => {
		if (!isValidGraphQLType) return [];
		if (!clipOrMeetingRecording) return [];
		if (!clipOrMeetingRecording.recapEngagementStats) return [];
		return clipOrMeetingRecording.recapEngagementStats.viewers || [];
	}, [clipOrMeetingRecording, isValidGraphQLType]);

	const sortedViewers = React.useMemo(() => {
		return [...viewers].sort((a, b) => b.viewCount - a.viewCount);
	}, [viewers]);
	const showWatchPercentage = useWatchStatsShowPercentageFeatureFlagOn();

	const showAllViewers = React.useCallback(() => setViewersToDisplay(sortedViewers), [sortedViewers]);

	const showFewerViewers = React.useCallback(() => {
		setViewersToDisplay(sortedViewers.slice(0, MAX_USERS_SHOWN));
	}, [sortedViewers]);

	const [viewersToDisplay, setViewersToDisplay] = React.useState<RecapEngagementViewersType>(sortedViewers);
	React.useEffect(() => {
		showFewerViewers();
	}, [showFewerViewers]);

	if (!isValidGraphQLType) return null;

	return (
		<>
			<RecapEngagementStatsCards
				totalViewCount={clipOrMeetingRecording?.recapEngagementStats?.totalViewCount}
				minutesSaved={isClip ? undefined : clipOrMeetingRecording?.recapEngagementStats?.minutesSaved}
				hasLegacyViews={clipOrMeetingRecording?.recapEngagementStats?.hasLegacyViews}
			/>
			{!clipOrMeetingRecording?.recapEngagementStats?.hasLegacyViews && (
				<HighlightSection
					title={
						<p className={styles.recapEngagementTitle}>
							Viewers
							<Tooltip content="See viewers and how much of the video has been watched.">
								<FontAwesomeIcon icon={faInfoCircleLight} size="sm" />
							</Tooltip>
						</p>
					}
					isCollapsed={isCollapsed}
					toggleCollapsed={toggleCollapsed}
				>
					{!viewers || !viewers.length ? (
						<p className={styles.emptySection}>There&apos;s no viewers data available for this recording.</p>
					) : (
						<main className={styles.recapEngagement} ref={sectionRef}>
							<h6>Viewers ({viewers.length})</h6>
							<h6>View count</h6>
							{showWatchPercentage ? <h6>%</h6> : <div />}

							{viewersToDisplay?.map((viewer: RecapEngagementViewersType[number]) => (
								<React.Fragment key={viewer.user?.email || viewer.displayName}>
									<p title={viewer.user?.fullName || viewer.displayName || "Anonymous"}>
										<Avatar
											grayscale={viewer.viewCount === 0}
											user={{
												fullName: viewer.user?.fullName || viewer.displayName || "Anonymous",
												image: viewer.user?.image || undefined,
											}}
											size="small"
											className={styles.participationAvatar}
										/>
										{viewer.user?.fullName || viewer.displayName || "Anonymous"}
									</p>
									<p>{viewer.viewCount > 0 ? viewer.viewCount : "-"}</p>
									{showWatchPercentage ? (
										<p>{viewer.watchPercentage ? `${Math.round(viewer.watchPercentage * 100)}%` : "-"}</p>
									) : (
										<div />
									)}
								</React.Fragment>
							))}
							{viewers &&
								viewersToDisplay &&
								viewers.length > MAX_USERS_SHOWN &&
								(viewersToDisplay.length < viewers.length ? (
									<button className={styles.showAll} onClick={showAllViewers}>
										<FontAwesomeIcon icon={faAngleDown} /> Show all
									</button>
								) : (
									<button className={styles.showAll} onClick={showFewerViewers}>
										<FontAwesomeIcon icon={faAngleUp} /> Show less
									</button>
								))}
						</main>
					)}
				</HighlightSection>
			)}
		</>
	);
};

const ParticipationSection = ({ participation }: { participation: UserStatsType }) => {
	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => setIsCollapsed(!isCollapsed), [isCollapsed]);
	const sectionRef = React.useRef<HTMLElement>(null);
	const [usersToDisplay, setUsersToDisplay] = React.useState<UserStatsType>();

	const sortedParticipation = React.useMemo(() => {
		if (!participation) return;
		return [...participation].sort((a, b) => {
			if (a.absent && b.absent) return a.fullName.localeCompare(b.fullName);
			return a.absent !== b.absent
				? Number(a.absent) - Number(b.absent)
				: a.totalTalkTime !== b.totalTalkTime
					? b.totalTalkTime - a.totalTalkTime
					: a.percentage !== b.percentage
						? b.percentage - a.percentage
						: a.fullName.localeCompare(b.fullName);
		});
	}, [participation]);

	React.useEffect(() => {
		setUsersToDisplay(sortedParticipation?.slice(0, MAX_USERS_SHOWN));
		return () => {
			setUsersToDisplay(undefined);
		};
	}, [sortedParticipation]);

	const showAllParticipants = React.useCallback(() => setUsersToDisplay(sortedParticipation), [sortedParticipation]);

	const showFewerParticipants = React.useCallback(() => {
		setUsersToDisplay(sortedParticipation?.slice(0, MAX_USERS_SHOWN));
	}, [sortedParticipation]);

	return (
		<HighlightSection title="Participation" isCollapsed={isCollapsed} toggleCollapsed={toggleCollapsed}>
			{!participation || !participation.length ? (
				<p className={styles.emptySection}>
					There is no participation data available for this recording. This is likely due to an issue during
					transcription.
				</p>
			) : (
				<main className={styles.participation} ref={sectionRef}>
					<h6>Speaker name</h6>
					<h6>Talk time</h6>
					<h6>%</h6>
					{usersToDisplay?.map(participant => {
						const totalTalkMins = participant.totalTalkTime / 60;
						const totalTalkStr = participant.absent
							? "-"
							: totalTalkMins > 0 && totalTalkMins < 1
								? "<1 min"
								: `${Math.round(totalTalkMins)} min`;

						const percentage = participant.percentage;
						const percentageStr = participant.absent
							? "-"
							: percentage > 0 && percentage < 1
								? "<1%"
								: `${Math.round(percentage)}%`;

						return (
							<React.Fragment key={`${participant.fullName}-${participant.percentage}`}>
								<p title={participant.fullName} className={cx({ [styles.absent]: participant.absent })}>
									<Avatar
										user={{
											fullName: participant.fullName,
											image: participant.image ?? undefined,
											isGuest: participant.isGuest ?? undefined,
											isTopUser: participant.isTopUser ?? undefined,
										}}
										size="small"
										className={styles.participationAvatar}
									/>
									{participant.fullName}
								</p>
								<p className={cx({ [styles.absent]: participant.absent })}>{totalTalkStr}</p>
								<p className={cx({ [styles.absent]: participant.absent })}>{percentageStr}</p>
							</React.Fragment>
						);
					})}
					{participation &&
						usersToDisplay &&
						participation?.length > MAX_USERS_SHOWN &&
						(usersToDisplay.length < participation.length ? (
							<button className={styles.showAll} onClick={showAllParticipants}>
								<FontAwesomeIcon icon={faAngleDown} /> Show all
							</button>
						) : (
							<button className={styles.showAll} onClick={showFewerParticipants}>
								<FontAwesomeIcon icon={faAngleUp} /> Show less
							</button>
						))}
				</main>
			)}
		</HighlightSection>
	);
};

const AttendanceSection = ({ attendance, recordingStart }: { attendance: UserStatsType; recordingStart: string }) => {
	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => setIsCollapsed(!isCollapsed), [isCollapsed]);
	const sectionRef = React.useRef<HTMLElement>(null);
	const [usersToDisplay, setUsersToDisplay] = React.useState<UserStatsType>();

	const sortedAttendance = React.useMemo(() => {
		if (!attendance) return;
		return [...attendance].sort((a, b) => {
			if (a.absent && b.absent) return a.fullName.localeCompare(b.fullName);
			return a.absent !== b.absent
				? Number(a.absent) - Number(b.absent)
				: a.joinedAt !== b.joinedAt
					? a.joinedAt - b.joinedAt
					: a.leftAt !== b.leftAt
						? b.leftAt - a.leftAt
						: a.fullName.localeCompare(b.fullName);
		});
	}, [attendance]);

	React.useEffect(() => {
		setUsersToDisplay(sortedAttendance?.slice(0, MAX_USERS_SHOWN));
		return () => {
			setUsersToDisplay(undefined);
		};
	}, [sortedAttendance]);

	const showAllAttendees = React.useCallback(() => setUsersToDisplay(sortedAttendance), [sortedAttendance]);

	const showFewerAttendees = React.useCallback(() => {
		setUsersToDisplay(sortedAttendance?.slice(0, MAX_USERS_SHOWN));
	}, [sortedAttendance]);

	return (
		<HighlightSection title="Attendance" isCollapsed={isCollapsed} toggleCollapsed={toggleCollapsed}>
			{!attendance || !attendance.length ? (
				<p className={styles.emptySection}>
					There is no attendance data available for this recording. This is likely due to the recording being older or
					an issue during transcription.
				</p>
			) : (
				<main className={styles.attendance} ref={sectionRef}>
					<h6>Attendee</h6>
					<h6>Joined</h6>
					<h6>Left</h6>
					{usersToDisplay?.map(attendee => (
						<React.Fragment key={`${attendee.fullName}-${attendee.joinedAt}`}>
							<p title={attendee.fullName} className={cx({ [styles.absent]: attendee.absent })}>
								<Avatar
									user={{
										fullName: attendee.fullName,
										image: attendee.image ?? undefined,
										isGuest: attendee.isGuest ?? undefined,
										isTopUser: attendee.isTopUser ?? undefined,
									}}
									size="small"
									className={styles.participationAvatar}
								/>
								{attendee.fullName}
							</p>
							<p className={cx({ [styles.absent]: attendee.absent })}>
								{attendee.absent
									? "-"
									: dateStringPlusNSeconds(recordingStart, attendee.joinedAt).toLocaleTimeString("en-CA", {
											timeStyle: "short",
										})}
							</p>
							<p className={cx({ [styles.absent]: attendee.absent })}>
								{/* leftAt is 0 if there is no recording duration (unlikely) */}
								{attendee.absent || attendee.leftAt === 0
									? "-"
									: dateStringPlusNSeconds(recordingStart, attendee.leftAt).toLocaleTimeString("en-CA", {
											timeStyle: "short",
										})}
							</p>
						</React.Fragment>
					))}
					{attendance &&
						usersToDisplay &&
						attendance.length > MAX_USERS_SHOWN &&
						(usersToDisplay.length < attendance.length ? (
							<button className={styles.showAll} onClick={showAllAttendees}>
								<FontAwesomeIcon icon={faAngleDown} /> Show all
							</button>
						) : (
							<button className={styles.showAll} onClick={showFewerAttendees}>
								<FontAwesomeIcon icon={faAngleUp} /> Show less
							</button>
						))}
				</main>
			)}
		</HighlightSection>
	);
};
